import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Modal.module.css";
import i18n from "../../Services/i18n";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";

const ModalButtonsContext = createContext(null);
export const useModalButtons = () => useContext(ModalButtonsContext);

const Modal = forwardRef(function Modal(
  {
    title,
    icon,
    isOpen,
    isTop,
    onSave,
    onSaveAndClose,
    onClose,
    canClose,
    children,
    form,
    hideSaveButton = false,
    hideSaveAndCloseButton = false,
    wideMode = false,
    dangerMode = false,
    saveButtondisabled = false,
    generateReportLabel = "",
    hideGenerateReportButton = true,
    onGenerateReport,
    generateReportDisabled = false,
  },
  ref,
) {
  const dialog = useRef();
  const portalWrapperRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const [titleLocal, setTitleLocal] = useState(title);
  const [hideSaveLocal, setHideSaveLocal] = useState(!!hideSaveButton);
  const [hideSaveAndCloseLocal, setHideSaveAndCloseLocal] = useState(!!hideSaveAndCloseButton);
  const [genVisibleLocal, setGenVisibleLocal] = useState(!hideGenerateReportButton);
  const [genLabelLocal, setGenLabelLocal] = useState(generateReportLabel || "");
  const [genDisabledLocal, setGenDisabledLocal] = useState(!!generateReportDisabled);
  const genOnClickRef = useRef(onGenerateReport || null);
  const [closeLabelOverride, setCloseLabelOverride] = useState(null);

  const isTopMostModal = () => {
    const modalRoot = document.getElementById("modal-root");
    if (!modalRoot || !portalWrapperRef.current) return false;
    return modalRoot.lastElementChild === portalWrapperRef.current;
  };

  const buttonsApi = {
    setButtons: ({ save, saveAndClose } = {}) => {
      if (typeof save === "boolean") setHideSaveLocal(!save);
      if (typeof saveAndClose === "boolean") setHideSaveAndCloseLocal(!saveAndClose);
    },
    setTitle: (newTitle) => {
      if (typeof newTitle === "string") setTitleLocal(newTitle);
    },
    setGenerate: ({ visible, label, disabled, onClick } = {}) => {
      if (typeof visible === "boolean") setGenVisibleLocal(visible);
      if (typeof label === "string") setGenLabelLocal(label);
      if (typeof disabled === "boolean") setGenDisabledLocal(disabled);
      if (typeof onClick === "function") genOnClickRef.current = onClick;
    },
    setCloseLabel: (label) => {
      setCloseLabelOverride(typeof label === "string" ? label : null);
    },
  };

  useImperativeHandle(ref, () => ({
    close() {
      handleCloseButton();
    },
  }));

  useEffect(() => {
    setHideSaveLocal(!!hideSaveButton);
  }, [hideSaveButton]);

  useEffect(() => {
    setHideSaveAndCloseLocal(!!hideSaveAndCloseButton);
  }, [hideSaveAndCloseButton]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isTop) return;

      if (event.key === "Escape") {
        if (!isTopMostModal()) return;
        event.preventDefault();
        event.stopPropagation();
        handleCloseButton();
      }
    };

    if (isOpen) {
      setHideSaveLocal(!!hideSaveButton);
      setHideSaveAndCloseLocal(!!hideSaveAndCloseButton);
      setGenVisibleLocal(!hideGenerateReportButton);
      setGenLabelLocal(generateReportLabel || "");
      setGenDisabledLocal(!!generateReportDisabled);
      genOnClickRef.current = onGenerateReport || null;
      setCloseLabelOverride(null);
      setTitleLocal(title);

      if (isTop) {
        document.addEventListener("keydown", handleKeyDown);
      }

      setTimeout(() => {
        dialog.current?.show();
      }, 10);
    }

    return () => {
      if (isTop) {
        document.removeEventListener("keydown", handleKeyDown);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTop]);

  useEffect(() => {
    setGenDisabledLocal(!!generateReportDisabled);
    setGenVisibleLocal(!hideGenerateReportButton);
  }, [generateReportDisabled, hideGenerateReportButton]);

  const handleCloseButton = async () => {
    if (!isTop || closing) return;

    if (typeof canClose === "function") {
      const allowed = await canClose();
      if (!allowed) return;
    }

    setIsSaving(false);
    setClosing(true);

    setTimeout(() => {
      dialog.current?.close();
      setClosing(false);
      onClose(form);
    }, 300);
  };

  const handleSaveButton = async () => {
    setIsSaving(true);
    await onSave();
    setIsSaving(false);
  };

  const handleSaveAndCloseButton = async () => {
    setIsSaving(true);
    await onSaveAndClose();
    setIsSaving(false);
  };

  const handleGenerateReport = async () => {
    const fn = genOnClickRef.current;
    if (!fn || genDisabledLocal) return;
    setIsSaving(true);
    await fn();
    setIsSaving(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <div ref={portalWrapperRef}>
      <div className={`${styles.backdrop} ${closing ? styles.backdropClosing : ""}`} />
      <dialog
        onClick={(e) => e.stopPropagation()}
        ref={dialog}
        className={`${wideMode ? styles.dialogWide : styles.dialog} ${closing ? styles.dialogClosing : ""}`}>
        <div className={`${styles.dialogHeader} ${dangerMode ? styles.dialogHeaderDanger : ""}`}>
          <div className={styles.dialogTitle}>
            {icon && <img src={icon} alt="client icon" className={styles.dialogHeaderIcon} />}
            <span>{titleLocal}</span>
          </div>
          <button
            type="button"
            className={styles.dialogCloseButton}
            onClick={handleCloseButton}
            aria-label={i18n.t("Modal.DefaultCancelButton")}>
            X
          </button>
        </div>

        <div className={wideMode ? styles.dialogContentWide : styles.dialogContent}>
          <ModalButtonsContext.Provider value={buttonsApi}>{children}</ModalButtonsContext.Provider>
        </div>

        <div className={`${styles.dialogFooter} ${dangerMode ? styles.dialogFooterDanger : ""}`}>
          <div className={styles.dialogFooterLeft}>
            {genVisibleLocal && (
              <button
                type="button"
                className={`${styles.formSecondaryButton} ${genDisabledLocal ? styles.buttonDisabled : ""}`}
                onClick={handleGenerateReport}
                title={genLabelLocal}
                disabled={genDisabledLocal}>
                🖨️ {genLabelLocal}
              </button>
            )}
          </div>

          <div className={styles.dialogFooterRight}>
            {!hideSaveLocal && (
              <button
                type="button"
                className={`${dangerMode ? styles.formOkButtonDanger : styles.formOkButton} ${
                  saveButtondisabled ? styles.saveBtnDisabled : ""
                }`}
                onClick={handleSaveButton}
                disabled={saveButtondisabled}>
                {dangerMode ? i18n.t("Modal.ConfirmButton") : i18n.t("Modal.DefaultSaveButton")}
              </button>
            )}

            {!hideSaveAndCloseLocal && !dangerMode && (
              <button
                type="button"
                className={`${styles.formOkButton} ${saveButtondisabled ? styles.saveBtnDisabled : ""}`}
                onClick={handleSaveAndCloseButton}
                disabled={saveButtondisabled}>
                {i18n.t("Modal.DefaultSaveAndCloseButton")}
              </button>
            )}

            <button
              type="button"
              className={dangerMode ? styles.formNeutralCancelButton : styles.formCancelButton}
              onClick={handleCloseButton}>
              {closeLabelOverride ??
                (hideSaveLocal
                  ? i18n.t("Modal.DefaultCloseButton")
                  : i18n.t("Modal.DefaultCancelButton"))}
            </button>
          </div>
        </div>

        {isSaving && (
          <div className={styles.dialogSavingOverlay}>
            <LoadingSpinner />
          </div>
        )}
      </dialog>
    </div>,
    document.getElementById("modal-root"),
  );
});

export default Modal;
